/**
 * API response types — mirror api/_shared/repos/{providers,individuals,
 * affiliations,providerSearch}.ts wire shapes per Block 6.A round-2 plan
 * §6 (Path 3 source-derived empirical anchor).
 *
 * Hand-mirrored rather than imported from api/_shared/* because the
 * frontend tsconfig excludes the api/ subproject (server types must not
 * leak into the bundle). When a server-side type changes shape, the
 * corresponding interface here updates in lockstep — drift surfaces in
 * tests/integration/* (the falsification gate per ADR-024 §2.3).
 *
 * Date fields arrive as ISO-8601 strings on the wire (JSON serialization
 * of pg + Kysely Date objects). The `string` type below reflects what the
 * UI actually receives. Display-side formatting (e.g., "yyyy-MM" stripping
 * the T00:00:00.000Z component on M:N affiliation date columns per F-B)
 * happens in the dedicated formatters in `@/lib/format.ts`.
 */

import type { ClientRecordAccess } from './clientRecordAccess';

// Mirrors api/_shared/schema.ts:240–246 (PROVIDER_KINDS).
export type ProviderKind =
  | 'agency'
  | 'firm'
  | 'government-office'
  | 'individual-practitioner'
  | 'other';

// Mirrors api/_shared/repos/providerSearch.ts:57–72 (SearchResult).
export interface SearchResult {
  type: 'organization' | 'individual';
  id: string;
  display_name: string;
  /** F-C: always null per ADR-011 §4 cardinality correction; UI ignores. */
  parent_provider_id: string | null;
  /** The entry's details (22n), the record read's own rule: an
   *  organization's first subcategory or category, a person's title. */
  kind_label?: string | null;
  /** A person's specialties; null for an organization. */
  specialty?: string | null;
  /** A person's primary organization; null for an organization. */
  practice?: string | null;
  town?: string | null;
  phone?: string | null;
  email?: string | null;
  /** The address as text. */
  address?: string | null;
}

export interface SearchResponse {
  rows: SearchResult[];
  limit: number;
  offset: number;
  q: string;
  request_id: string;
}

// Mirrors api/_shared/repos/providers.ts:64–72 (ProviderListItem).
export interface ProviderListItem {
  id: string;
  category: string;
  kind: ProviderKind;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProviderListResponse {
  rows: ProviderListItem[];
  total: number;
  limit: number;
  offset: number;
  request_id: string;
}

// Mirrors api/_shared/repos/providers.ts ProviderServiceArea.
// Phase 1.8 (migration 0063): `id` is now the provider_service_areas row
// uuid PK; `tier` indicates which lookup table the row references;
// `short_name` populates for MSA/MD/CSA/ZIP tiers.
export type ProviderServiceAreaTier =
  | 'state'
  | 'county'
  | 'metro_division'
  | 'msa'
  | 'csa'
  | 'zip';

export interface ProviderServiceArea {
  id: string;
  tier: ProviderServiceAreaTier;
  slug: string;
  display_name: string;
  short_name: string | null;
  state_code: string | null;
  /** Backward-compat alias; equals `tier` for new rows. */
  area_type: 'country' | 'state' | 'county' | 'metro_division' | 'msa' | 'csa' | 'zip';
}

// Phase 1.8 — tier-table wire shapes for picker + client-side search.
export interface ServiceAreaCsaForSearch {
  id: string;
  slug: string;
  display_name: string;
  short_name: string;
  county_ids: string[];
}
export interface ServiceAreaMsaForSearch {
  id: string;
  slug: string;
  display_name: string;
  short_name: string;
  csa_id: string | null;
  county_ids: string[];
}
export interface ServiceAreaMetroDivisionForSearch {
  id: string;
  slug: string;
  display_name: string;
  short_name: string;
  msa_id: string;
  county_ids: string[];
}
export interface ServiceAreaZipForSearch {
  id: string;
  zip_code: string;
  city: string | null;
  county_id: string;
  county_display: string;
  md_id: string | null;
  msa_id: string;
  csa_id: string | null;
}

export interface ServiceAreaTiersResponse {
  csas: ServiceAreaCsaForSearch[];
  msas: ServiceAreaMsaForSearch[];
  metro_divisions: ServiceAreaMetroDivisionForSearch[];
  zips: ServiceAreaZipForSearch[];
  request_id: string;
}

export interface ProviderServiceAreaExclusion {
  provider_service_area_id: string;
  excluded_zip_id: string;
  zip_code: string;
}

// Mirrors api/_shared/repos/providers.ts ProviderSubcategoryRef (§G.3).
export interface ProviderSubcategoryRef {
  id: string;
  slug: string;
  display_name: string;
}
export interface ProviderSpecialtyRef {
  id: string;
  slug: string;
  display_name: string;
}

// Mirrors api/_shared/repos/providers.ts ProviderIndividualAffiliation
// (§G.2 — Block 6.B.3).
export interface ProviderIndividualAffiliation {
  /** pio.role — the role at THIS org. */
  role: string | null;
  /** pio.title — the title at THIS org (vs pi.title cross-org generic). */
  title: string | null;
  is_primary: boolean;
  start_date: string | null;
  end_date: string | null;
}

// Mirrors api/_shared/repos/providers.ts ProviderIndividualSummary.
// §G.2: now embeds affiliation-specific context.
export interface ProviderIndividualSummary {
  id: string;
  full_name: string;
  /** pi.title — cross-org generic title. */
  title: string | null;
  business_phone: string | null;
  email: string | null;
  is_referral_partner: boolean;
  needs_review: boolean;
  /** §G.2: the M:N row's affiliation-specific context. */
  affiliation: ProviderIndividualAffiliation;
}

// Block 6.F.3 — note_type discriminator (migration 0055). Lowercase
// enum-style string driving the CM Notes badge UI per peace-hub
// TYPE_STYLES (ResourceDetail.jsx:7-11).
export type NoteType = 'tip' | 'warning' | 'general';

// Mirrors api/_shared/repos/providers.ts:117-140 (ProviderNoteSummary
// discriminated union — author_user_id + author_display_name absent on
// anonymous variant). Block 6.F.3 added note_type + author_display_name
// (the latter JOINed from users at handler).
export type ProviderNoteSummary =
  | {
      version: number;
      body: string;
      note_type: NoteType;
      is_anonymous: false;
      author_user_id: string;
      author_display_name: string;
      created_at: string;
    }
  | {
      version: number;
      body: string;
      note_type: NoteType;
      is_anonymous: true;
      created_at: string;
    };

// Mirrors api/_shared/repos/providerNotes.ts:54-85 (ProviderNote
// discriminated union — full history list shape, returned by
// GET /providers/{id}/notes).
export type ProviderNote =
  | {
      id: string;
      provider_id: string;
      version: number;
      body: string;
      note_type: NoteType;
      is_anonymous: false;
      author_user_id: string;
      author_display_name: string;
      created_at: string;
      /** Whether the reader wrote this note; absent reads as not hers. */
      is_own?: boolean;
    }
  | {
      id: string;
      provider_id: string;
      version: number;
      body: string;
      note_type: NoteType;
      is_anonymous: true;
      created_at: string;
      /** Whether the reader wrote this note; absent reads as not hers. */
      is_own?: boolean;
    };

// Mirrors api/_shared/repos/individualNotes.ts (IndividualNote): a team
// note on a Directory person, ProviderNote's shape keyed by individual_id.
export type IndividualNote =
  | {
      id: string;
      individual_id: string;
      version: number;
      body: string;
      note_type: NoteType;
      is_anonymous: false;
      author_user_id: string;
      author_display_name: string;
      created_at: string;
      /** Whether the reader wrote this note; absent reads as not hers. */
      is_own?: boolean;
    }
  | {
      id: string;
      individual_id: string;
      version: number;
      body: string;
      note_type: NoteType;
      is_anonymous: true;
      created_at: string;
      /** Whether the reader wrote this note; absent reads as not hers. */
      is_own?: boolean;
    };

export interface IndividualNotesListResponse {
  rows: IndividualNote[];
  request_id: string;
}

/** What the team-notes card reads from a note, on an organization or a person. */
export type TeamNote = ProviderNote | IndividualNote;

export interface ProviderNotesListResponse {
  rows: ProviderNote[];
  request_id: string;
}

// Mirrors api/_shared/repos/providers.ts:185-211 (ProviderDetail).
// Block 6.F.4 added updated_by_user_id + updated_by_display_name for
// the "Last edited by..." display per design plan §3.8.
export interface ProviderDetail {
  id: string;
  category: string;
  kind: ProviderKind;
  name: string;
  address: string | null;
  /** The address's parts; null on every part: text only. */
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  /** On a save's answer: what the save did to the map. */
  geocode_outcome?: 'placed' | 'approximate' | 'no_match' | 'unavailable' | 'not_attempted';
  phone: string | null;
  email: string | null;
  website: string | null;
  // Phase 1.7.2 (migration 0060): legacy `notes` column dropped.
  // Notes for an org live in provider_notes (versioned); consumed
  // via the separate listProviderNotes endpoint.
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Block 6.F.4 — "Last edited by..." display source.
  // updated_by_user_id is always populated (NOT NULL column from
  // migration 0024 audit-columns); updated_by_display_name is JOIN-
  // resolved from users.display_name via LEFT JOIN — null when the
  // LEFT JOIN misses (orphan reference; user is_active=false or deleted).
  // UI omits the "by [name]" suffix when display_name is null per
  // design plan §3.8 L2 fix.
  updated_by_user_id: string;
  updated_by_display_name: string | null;
  // Phase 1.7 A5 cohort (migration 0058). NULL = "not assessed" per
  // column comments at the migration boundary. Display surfaces on
  // OrganizationDetail render conditionally on value present per the
  // "current placeholder behavior" pattern.
  // Phase 1.7.1 (migration 0059): `referral_partner` removed — relocated
  // to provider_individuals.is_referral_partner (the column on the
  // contact person, not on the org).
  //
  // Phase 1.10 (migration 0069): 4 A5 text columns dropped from
  // provider_organizations + promoted to FK-backed catalog + assignments.
  // The 3 single-value families (medicaid + dementia_unit + gender_restrictions)
  // keep their scalar wire shape per r2 disposition #10 — rederived
  // server-side from provider_highlight_assignments. SDM evolves to plural
  // per r2 disposition #11 (`service_delivery_model: string | null` →
  // `service_delivery_models: string[]`).
  medicaid: string | null;
  dementia_unit: boolean | null;
  phone_2: string | null;
  fax: string | null;
  payment_types: string[] | null;
  /**
   * Phase 1.10 r2 disposition #11 — plural array of display_names
   * rederived from provider_service_model_assignments. Empty array means
   * "no delivery model assigned". The 39 previously-NULL multi-value
   * orgs from Phase 1.9.2's audit table flow through as N-element arrays.
   */
  service_delivery_models: string[];
  gender_restrictions: string | null;
  service_areas: ProviderServiceArea[];
  subcategories: ProviderSubcategoryRef[];
  specialties: ProviderSpecialtyRef[];
  individuals: ProviderIndividualSummary[];
  latest_note: ProviderNoteSummary | null;
  /**
   * Phase 1.13.2 (migration 0072) — tenant-scoped curation flag with
   * freshness audit. Atomic trio: when is_recommended=true, all three
   * are populated; when false, recommended_at + recommended_by_user_id
   * are NULL. recommended_by_display_name is LEFT JOIN-resolved
   * server-side; NULL when the recommender's row is_active=false or
   * the FK is orphaned — UI tooltip degrades to date-only.
   */
  is_recommended: boolean;
  recommended_at: string | null;
  recommended_by_user_id: string | null;
  recommended_by_display_name: string | null;
  request_id: string;
}

// Block 6.F.2 — bulk-load shapes for client-side fuse.js search.
// Mirrors api/_shared/repos/providers.ts:ProviderForSearch +
// individuals.ts:IndividualForSearch.
//
// counties: string[] is M5-derived server-side from service_areas
// where area_type='county'. service_areas[] discriminated shape NOT
// included in this response (lean payload; getProvider retains).
//
// Phase 1.9.3 — A5 cohort (medicaid + dementia_unit + service_delivery_model
// + gender_restrictions) is on the bulk wire as of this phase. Powers
// the Features pill (Medicaid + Memory Care + Restrictions families),
// the Delivery method top-level filter, and the ResourceCard badges.
// Bug-fix history: Phase 1.9.1 added the filter dispatch + card badges
// referencing these fields but missed the SELECT extension — the
// Features filter was silently returning 0 results until 1.9.3 wired
// the columns end-to-end. payment_types joined the search payload as a
// filter dimension powering the "Payment type" pill (below); still also on
// the detail surface via getProvider.
//
// IndividualForSearch.specialties is `string | null` per peace-os
// schema (peace-hub modeled as string[], peace-os imported as
// free-text from wave-2026; H1 absorption).

export interface ProviderForSearch {
  id: string;
  name: string;
  category_slug: string;
  kind: ProviderKind;
  phone: string | null;
  address: string | null;
  // Payment type filter facet (provider_organizations.payment_types jsonb
  // string[]). Powers the "Payment type" FilterBar pill; mirrors the server
  // ProviderForSearch field.
  payment_types: string[] | null;
  notes: string | null;
  /**
   * E2 follow-up — current-note presence: true iff the provider has >=1 note
   * whose author is still visible in the tenant (mirrors listProviderNotes'
   * INNER JOIN users — what the detail actually renders). The "Has notes" filter
   * keys on THIS, not the all-versions `notes` blob above (which retains bodies
   * from authors no longer in the tenant, for fuzzy search).
   */
  has_current_note: boolean;
  subcategories: Array<{ slug: string; display_name: string }>;
  specialties: Array<{ slug: string; display_name: string }>;
  /**
   * Phase 1.9 + 1.10: highlight tags driving the Features filter pill
   * (sibling of Specialty). Tenant-global lookup via provider_highlights;
   * tenant-scoped M:N via provider_highlight_assignments.
   *
   * Phase 1.10 (migration 0069): multi-family catalog. `family` carried
   * through on the wire so FilterBar's Features pill partitions selected
   * items by family at filter-dispatch time. Scalar fields below
   * (medicaid / dementia_unit / gender_restrictions) are rederived from
   * this array server-side to preserve the prior wire shape (no client
   * churn for scalar reads).
   */
  highlights: Array<{
    slug: string;
    display_name: string;
    family: 'medicaid' | 'memory_care' | 'gender_restrictions';
  }>;
  /**
   * Phase 1.8: counties are derived from effective-coverage expansion
   * (state→all NJ counties; MSA→all counties via service_area_zips JOIN;
   * etc.). Preserves the M5 flat shape for backward-compat with fuse +
   * FilterBar.
   */
  counties: string[];
  /**
   * Phase 1.8.3: state codes (e.g. ['NJ']) where the provider has
   * tier='state' coverage. ResourceCard renders these instead of the
   * expanded county list when non-empty.
   */
  state_coverage: string[];
  contacts: string[];
  /**
   * Phase 1.10 r2 disposition #10 — scalar fields rederived server-side
   * from the highlights[] junction grouped by family (no client churn
   * for the 3 single-value scalar reads). NULL means "not assigned".
   */
  medicaid: string | null;
  dementia_unit: boolean | null;
  gender_restrictions: string | null;
  /**
   * Phase 1.10 r2 disposition #11 — plural array of display_names
   * rederived from provider_service_model_assignments. Empty array means
   * "no delivery model assigned". The Delivery method filter pill iterates
   * with .some() match against this array; the ResourceCard renders one
   * chip per element.
   */
  service_delivery_models: string[];
  /**
   * Phase 1.13.2 (migration 0072) — tenant-scoped curation flag carried
   * onto search results so the RecommendedBadge renders in ResourceCard
   * without a follow-up detail fetch. recommended_at is ISO string for
   * JSON-wire transport (decoded into a Date inside isRecommendationStale).
   * recommended_by_display_name is NULL-tolerant per the orphan-FK pattern.
   */
  is_recommended: boolean;
  recommended_at: string | null;
  recommended_by_display_name: string | null;
}

/**
 * Phase 1.8 — extended bulk-load response from /v1/providers/all. Adds
 * tier-table arrays alongside the providers list for client-side city/
 * ZIP/MSA search resolution.
 */
export interface ProvidersForSearchExtendedResponse {
  rows: ProviderForSearch[];
  service_area_csas: ServiceAreaCsaForSearch[];
  service_area_msas: ServiceAreaMsaForSearch[];
  service_area_metro_divisions: ServiceAreaMetroDivisionForSearch[];
  service_area_zips: ServiceAreaZipForSearch[];
  request_id: string;
}

export interface IndividualForSearch {
  id: string;
  full_name: string;
  title: string | null;
  business_phone: string | null;
  cell_phone: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  specialties: string | null;
  is_referral_partner: boolean;
  // Recommended people (migration 0197): the card shows the badge and the
  // default sort floats recommended people first, as for organizations.
  is_recommended: boolean;
  recommended_at: string | null;
  recommended_by_display_name: string | null;
  current_org_ids: string[];
  current_org_names: string[];
  /** At least one team note (individual_notes) by an author visible in the
   *  tenant: ProviderForSearch.has_current_note's twin. Optional so a cached
   *  older payload still reads; absent counts as none. */
  has_current_note?: boolean;
}

export interface ProvidersForSearchResponse {
  rows: ProviderForSearch[];
  request_id: string;
}

export interface IndividualsForSearchResponse {
  rows: IndividualForSearch[];
  request_id: string;
}

// Mirrors api/_shared/repos/individuals.ts (IndividualListItem post-§4.1).
export interface IndividualListItem {
  id: string;
  full_name: string;
  title: string | null;
  business_phone: string | null;
  email: string | null;
  is_referral_partner: boolean;
  needs_review: boolean;
  is_active: boolean;
  last_verified_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  // Recommended people (migration 0197): the same curation trio as an
  // organization. When is_recommended is false the other two are null.
  is_recommended: boolean;
  recommended_at: string | null;
  recommended_by_user_id: string | null;
}

export interface IndividualListResponse {
  rows: IndividualListItem[];
  total: number;
  limit: number;
  offset: number;
  request_id: string;
}

// §4.1 NEW: AffiliationWithOrg embedded in IndividualDetail.
// Mirrors api/_shared/repos/individuals.ts AffiliationWithOrg.
//
// start_date / end_date are pg `date` columns (migration 0047), not
// timestamptz: they arrive as the day's 'YYYY-MM-DD' text.
export interface AffiliationWithOrg {
  provider_id: string;
  provider_name: string;
  provider_kind: ProviderKind;
  /** Org's primary category slug (joined server-side) — drives the category
   *  icon-chip + colors on the affiliation card. */
  provider_category: string;
  role: string | null;
  title: string | null;
  is_primary: boolean;
  start_date: string | null;
  end_date: string | null;
}

// Mirrors api/_shared/repos/individuals.ts IndividualDetail (post §4.1).
// Block 6.F.4 added updated_by_user_id + updated_by_display_name for
// the "Last edited by..." display per design plan §3.8.
export interface IndividualDetail extends IndividualListItem {
  cell_phone: string | null;
  /** A person's fax (0242). Optional on the wire type so an older cached
   *  payload still reads; the server always sends it. */
  fax?: string | null;
  address: string | null;
  /** The address's parts; null on every part: text only. */
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  /** On a save's answer: what the save did to the map. */
  geocode_outcome?: 'placed' | 'approximate' | 'no_match' | 'unavailable' | 'not_attempted' | 'shown_with_organization';
  specialties: string | null;
  notes: string | null;
  last_updated: string | null;
  // Block 6.F.4 — "Last edited by..." display source. Same shape as
  // ProviderDetail above; null display_name omits the "by [name]"
  // suffix in the UI per L2 fix.
  updated_by_user_id: string;
  updated_by_display_name: string | null;
  /**
   * §4.1: affiliations with joined org name + kind. Order: is_primary
   * DESC, start_date DESC NULLS LAST. Empty array if individual has no
   * active affiliations.
   */
  affiliations: AffiliationWithOrg[];
  request_id: string;
  // Who recommended this person, by display name; null when not recommended.
  recommended_by_display_name: string | null;
}

// Mirrors api/_shared/repos/directoryConnections.ts ConnectedClient —
// the directory→clients reverse read (directory care-web, 2026-07-10).
export interface ConnectedClient {
  client_id: string;
  display_name: string;
  /** clients.is_active — archived clients stay listed, rendered muted. */
  is_active: boolean;
}

export interface ConnectedClientsResponse {
  clients: ConnectedClient[];
  request_id: string;
}

// Mirrors api/_shared/repos/lookups.ts LookupRow.
export interface LookupRow {
  id: string;
  slug: string;
  display_name: string;
}

// Mirrors api/_shared/repos/lookups.ts ServiceAreaLookupRow.
export interface ServiceAreaLookupRow extends LookupRow {
  area_type: 'country' | 'state' | 'county' | 'metro' | 'region' | 'zip';
  state_code: string | null;
  parent_id: string | null;
}

// Block 6.C.1 admin-surface row shapes — extend the picker-facing types
// with the editorial / cardinality columns the admin UI needs. Mirrors
// api/_shared/repos/lookups.ts LookupAdminRow + ServiceAreaAdminRow.
//
// service_areas asymmetry: the row has no deprecated_at column (migration
// 0042 — only is_active landed; deprecation history lives in phi_access_log
// per ADR-020 §Decision 2). ServiceAreaAdminRow omits the field accordingly.
export interface LookupAdminRow extends LookupRow {
  is_active: boolean;
  deprecated_at: string | null;
  record_count: number;
}

export interface ServiceAreaAdminRow extends ServiceAreaLookupRow {
  is_active: boolean;
  record_count: number;
}

// =====================================================================
// Block 6.C.2 / ADR-021 — Suggested edits workflow
// =====================================================================
//
// Mirrors api/_shared/repos/suggestedEdits.ts SuggestedEditRow +
// SuggestedEditWithJoinDisplay. timestamptz columns serialize to
// ISO-8601 strings on the wire (per ADR-024 §"Empirical anchor
// disposition" + types.ts header note on date serialization).
export type SuggestedEditTargetType = 'organization' | 'individual';
export type SuggestedEditStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

export interface SuggestedEditRow {
  id: string;
  tenant_id: string;
  target_type: SuggestedEditTargetType;
  target_id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  rationale: string | null;
  status: SuggestedEditStatus;
  suggested_by_user_id: string;
  reviewed_by_user_id: string | null;
  review_note: string | null;
  created_at: string;
  reviewed_at: string | null;
  updated_at: string;
}

// LIST + GET endpoints return joined display fields alongside the row.
export interface SuggestedEditWithJoinDisplay extends SuggestedEditRow {
  target_display: string;
  suggester_display: string;
  reviewer_display: string | null;
}

export interface SuggestedEditListResponse {
  rows: SuggestedEditWithJoinDisplay[];
  total: number;
  limit: number;
  offset: number;
  request_id: string;
}

export interface SuggestedEditCountResponse {
  count: number;
  request_id: string;
}

// =====================================================================
// Block 6.E / ADR-017 §"Bulk-adding 12 organizations" — Bulk add
// =====================================================================
//
// Mirrors api/_shared/repos/bulkAdd.ts wire shapes. The 7th column
// `kind` was added at round 2 per Q-S5 empirical (wave-2026 is 100%
// 'other'; default-when-blank is 'other' to match the dataset).

export const PROVIDER_KINDS_FOR_UI = [
  'agency',
  'firm',
  'government-office',
  'individual-practitioner',
  'other',
] as const;
export type ProviderKindForUI = (typeof PROVIDER_KINDS_FOR_UI)[number];

export interface BulkAddDuplicateCandidate {
  id: string;
  name: string;
  address: string | null;
  similarity: number;
}

export interface BulkAddPendingLookupAdd {
  type: 'subcategory' | 'service_area';
  slug: string;
  display_name: string;
  area_type?: 'country' | 'state' | 'county' | 'metro' | 'region' | 'zip';
  state_code?: string | null;
  referenced_by_row_count: number;
}

/** A bulk-add row's address: its five parts (a city and a state at least). */
export interface BulkAddAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export interface BulkAddParsedRowPreview {
  row_index: number;
  name: string | null;
  phone: string | null;
  /** The five parts, or null for no address. */
  address: BulkAddAddress | null;
  category_slug: string | null;
  category_known: boolean;
  subcategory_slugs: ReadonlyArray<string>;
  service_area_slugs: ReadonlyArray<string>;
  kind_raw: string | null;
  kind_known: boolean;
  errors: ReadonlyArray<string>;
  duplicate_candidates: ReadonlyArray<BulkAddDuplicateCandidate>;
}

export interface BulkAddPreviewResponse {
  rows: BulkAddParsedRowPreview[];
  pending_lookup_adds: BulkAddPendingLookupAdd[];
  summary: {
    valid_rows: number;
    error_rows: number;
    rows_with_duplicates: number;
    new_lookup_adds: number;
  };
  request_id: string;
}

export interface BulkAddCommitRow {
  row_index: number;
  name: string;
  phone: string | null;
  /** The five parts, or null for no address. */
  address: BulkAddAddress | null;
  category_slug: string;
  subcategory_slugs: ReadonlyArray<string>;
  service_area_slugs: ReadonlyArray<string>;
  kind: ProviderKindForUI;
  disposition: 'create' | 'skip_as_duplicate' | 'skip_with_error';
  duplicate_of_org_id?: string;
}

export interface BulkAddCommitRequest {
  rows: BulkAddCommitRow[];
  pending_lookup_adds: BulkAddPendingLookupAdd[];
  approved_lookup_seeding: boolean;
}

export interface BulkAddCommitResponse {
  created_org_ids: string[];
  skipped_as_duplicate: number;
  skipped_with_error: number;
  added_subcategories: number;
  added_service_areas: number;
  request_id: string;
}

// Lookup table identifier for admin route + repo dispatch. Matches
// api/_shared/repos/lookups.ts LookupTable.
export type LookupTable =
  | 'provider_subcategories'
  | 'provider_specialties'
  | 'provider_highlights'
  | 'provider_service_models'
  | 'service_areas';

export type MergeableLookupTable =
  | 'provider_subcategories'
  | 'provider_specialties'
  | 'provider_highlights';

// URL-shape identifier — what the admin tab uses in its `?type=` query
// param and what the API path uses (e.g. `/lookups/subcategories`). The
// table-name form (`provider_subcategories`) is the DB schema identity;
// the URL form (`subcategories`) is the route identity.
//
// Phase 1.10 — `service-model` (NEW) for the provider_service_models
// catalog. `highlights` URL preserved for backwards-compat per r2
// disposition #4; the label rendered in the tab bar updates to
// "Features" via LOOKUP_TYPE_URL_TO_LABEL.
export type LookupTypeUrl =
  | 'subcategories'
  | 'specialties'
  | 'highlights'
  | 'service-model'
  | 'service-areas';

export const LOOKUP_TYPE_URL_TO_TABLE: Record<LookupTypeUrl, LookupTable> = {
  subcategories: 'provider_subcategories',
  specialties: 'provider_specialties',
  highlights: 'provider_highlights',
  'service-model': 'provider_service_models',
  'service-areas': 'service_areas',
};

export const LOOKUP_TYPE_URL_TO_LABEL: Record<LookupTypeUrl, string> = {
  subcategories: 'Subcategories',
  specialties: 'Specialties',
  // Phase 1.10 r2 disposition #4 — UI label "Features"; URL `?type=highlights`
  // preserved for backwards-compat.
  highlights: 'Features',
  'service-model': 'Delivery method',
  'service-areas': 'Service areas',
};

/**
 * Phase 1.10 — `family` extension on admin highlight rows for the
 * Features tab family-grouped display (Medicaid: 5 / Memory Care: 1 /
 * Gender Restrictions: 2). Only populated when the admin endpoint
 * returns highlight rows; other lookup types omit this field.
 */
export interface HighlightAdminRow extends LookupAdminRow {
  family: 'medicaid' | 'memory_care' | 'gender_restrictions' | 'legacy';
}

// Phase 1.6 — frontend mirror of api/_shared/schema.ts:UserTenantRole.
// §m boundary discipline: no cross-import between api/ and src/; this
// type is duplicated by convention. If the server enum changes, this
// updates in lockstep.
//
// X.5.4 — exec_support was added server-side at X.5.1 (migration 0083)
// but this mirror was NOT updated in lockstep, so the Team tab (which
// imports UserTenantRole from here) treated Laila's role as out-of-union:
// ROLE_DISPLAY had no key for it and the chip rendered "undefined". Added
// here to restore lockstep; src/lib/capabilities.ts already carries it.
export type UserTenantRole =
  | 'owner'
  | 'primary_cm'
  | 'supervising_cm'
  | 'admin_support'
  | 'data_steward'
  | 'mso_staff'
  | 'exec_support'
  // Board observer (migration 0152) — read-only board-member role.
  | 'board_observer'
  // The Directory Editor: edits the directory, no client records by default.
  | 'directory_editor'
  // Accounting (0220): billing, payroll hours and reports; no client record.
  | 'accounting';

// Mirrors api/_shared/auth.ts whoami response.
export interface WhoamiResponse {
  user_id: string;
  tenant_id: string;
  /**
   * Phase 1.6 — nullable for the not_provisioned outcome. SPA route
   * guard at AppShell reads `outcome === 'not_provisioned'` and renders
   * the access-denied landing before any role-dependent UI mounts.
   */
  role: string | null;
  active_engagements: unknown[];
  request_id: string;
  /**
   * Phase 1.6 — outcome flag. Absent / 'success' means normal auth;
   * 'not_provisioned' means the user authenticated but has no
   * user_tenants membership, no service_engagement, and no pending
   * grant for this tenant. Renderer at AppShell short-circuits to the
   * NotProvisionedLanding component.
   */
  outcome?: 'success' | 'not_provisioned';
  /**
   * ADR-0046 (migration 0157) — the server-computed per-tenant EFFECTIVE
   * capability set (static role defaults ± this tenant's
   * role_capability_overrides). When present, src/lib/capabilities.ts's
   * predicates read THIS instead of deriving from role, so owner
   * Permissions-screen changes reach affordance gating without a deploy.
   * Optional for back-compat: absent (older cached shape) falls back to
   * the role-derived static defaults. Affordance-level only — the
   * server's requireCapability is the authorization boundary.
   */
  capabilities?: string[];
  /**
   * The tenant's client record setting (tenants.config.access.client_records),
   * read by the server on the same request. canReadEveryClient is its only
   * reader: under 'all_staff' an internal view_engagement_clients holder
   * reads every client. Optional for older cached whoami shapes; absent
   * reads as 'own_service', the narrower answer.
   */
  client_record_access?: ClientRecordAccess;
  /**
   * Migration 0134 — the acting user's per-staff SPICE flag
   * (staff_roster.does_spice). Rides whoami so the rate-band pickers
   * (composer / pre-billing) can scope band options by role + does_spice
   * via bandVisibility.bandsForActor. Optional for older cached whoami
   * shapes; read as `whoami?.does_spice ?? false`. NOT a capability — a
   * staff attribute that composes additively with the role.
   */
  does_spice?: boolean;
  /**
   * Migration 0143 (Design Pass 4 §A) — the acting user's per-staff
   * care-management flag (staff_roster.does_care_management), the does_spice
   * twin. Rides whoami so the rate-band pickers scope band options by role +
   * does_care_management + does_spice via bandVisibility.bandsForActor.
   * Optional for older cached whoami shapes; read as
   * `whoami?.does_care_management ?? false`. NOT a capability.
   */
  does_care_management?: boolean;
  /**
   * The Clients-landing predicate (ClientsLandingGate, routes.tsx): true
   * when the viewer's roster row is responsible for any active client —
   * primary care manager, SPICE therapist, or SPICE visit-plan cover —
   * regardless of what does_care_management / does_spice say they're
   * allowed to log. Optional for older cached whoami shapes; a viewer
   * with view_all_clinical reads `whoami?.has_caseload === false` to
   * decide the roster opens instead of "Your day" (undefined stays on
   * "Your day", the safer default).
   */
  has_caseload?: boolean;
  /**
   * Migration 0161 (CM intro email) — the acting user's signature name
   * (their staff_roster full_name, else a real-name users.display_name;
   * null when neither is on file). The "Copy intro email" block signs the
   * valediction with this. Optional for older cached whoami shapes; read
   * as `whoami?.display_name ?? null` — null renders a highlighted
   * [Your name] placeholder instead of a wrong or empty signature.
   */
  display_name?: string | null;
}

// SWA /.auth/me principal shape.
export interface ClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

export interface AuthMeResponse {
  clientPrincipal: ClientPrincipal | null;
}

// =====================================================================
// Block 6.F.5 — admin-review timeline + badge count
// =====================================================================
//
// Mirrors api/_shared/repos/adminReview.ts wire shapes. The timeline
// UNIONs change_log + provider_notes + suggested_edits with H1 SQL-
// boundary anonymity suppression on the note branch (anonymous rows
// emit actor_user_id=null + actor_display_name=null + is_anonymous=true).

export type AdminTimelineSource =
  | 'change'
  | 'note'
  | 'suggestion'
  | 'resource_proposal'
  | 'resource_approval';

export type AdminTimelineEventKind =
  | 'direct_edit'
  | 'applied_suggestion'
  | 'note_create'
  | 'suggestion_submit'
  | 'resource_propose'
  | 'resource_approve';

// Phase 1.6 — 'user_tenant' added for the Team tab role-change emission.
// Honest only because the repo SQL allow-lists exactly these values on
// the change_log branch (the table holds many more target types). Widen
// together with the repo allow-list AND RecentActivityRow's
// fallbackTargetName (whose default arm covers out-of-union values).
export type AdminTimelineTargetType = 'organization' | 'individual' | 'user_tenant';

export interface AdminTimelineRow {
  source: AdminTimelineSource;
  row_id: string;
  /** ISO-8601 timestamp. */
  created_at: string;
  /**
   * Actor user id. NULL for anonymous notes per ADR-011 §5 SQL-boundary
   * suppression; also NULL for orphan references.
   */
  actor_user_id: string | null;
  /**
   * Actor display name. NULL when actor_user_id is NULL (LEFT JOIN
   * naturally returns NULL); renderer falls back to "Someone" for
   * is_anonymous=true.
   */
  actor_display_name: string | null;
  /**
   * Anonymity flag. TRUE only when source='note' AND the underlying
   * provider_notes row had is_anonymous=true. Renderer branches on this
   * flag explicitly rather than re-deriving from absence of actor_user_id.
   */
  is_anonymous: boolean;
  event_kind: AdminTimelineEventKind;
  target_type: AdminTimelineTargetType;
  target_id: string;
  /** Resolved target name; NULL for orphan references. */
  target_display: string | null;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  source_suggested_edit_id: string | null;
  /**
   * For event_kind='applied_suggestion' rows: the original suggester's
   * display_name (JOIN-resolved). Surfaces "Amanda approved Sarah's
   * suggested change" microcopy.
   */
  source_suggester_display_name: string | null;
  /**
   * Phase 1.6: "queued by" attribution for the rows auth_resolve_request
   * writes when it applies a pending grant. The join is keyed on the
   * target user (pending_grants.applied_user_id), so every user_tenant
   * row for a person whose invite has been applied carries it; the
   * renderer decides which sentences show it: the grant row (old_value
   * null, "joined as") and the self-authored is_active row a re-invite
   * writes ("rejoined"). Design plan r3 §D.
   */
  queued_by_user_id: string | null;
  queued_by_display_name: string | null;
  queued_at: string | null;
}

export interface AdminTimelineResponse {
  rows: AdminTimelineRow[];
  total: number;
  has_more: boolean;
  request_id: string;
}

export interface AdminBadgeCountResponse {
  /** Count of pending suggested_edits visible to the caller (server-side scoped by role). */
  pending_suggestions: number;
  /**
   * Phase 1.7 — count of pending suggested_resources visible to the caller
   * (server-side scoped by role). AppShell bell renders the sum of
   * pending_suggestions + pending_resources. Additive shape per design
   * plan r2 §9.1 forward-fit; 6.F.6 may extend further (bulk_in_progress,
   * pending_verifications, etc.) without breaking the AppShell consumer.
   */
  pending_resources: number;
  request_id: string;
}

// =====================================================================
// Phase 1.7 — suggested_resources wire shapes (migration 0058)
// =====================================================================

export type SuggestedResourceTargetType = 'organization' | 'individual';
export type SuggestedResourceStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

/**
 * Mirrors api/_shared/repos/suggestedResources.ts:SuggestedResourceWithJoinDisplay.
 * Wire dates are ISO strings (per the server's ::text cast in
 * loadOneSuggestedResourceWithJoins).
 */
export interface SuggestedResource {
  id: string;
  tenant_id: string;
  target_type: SuggestedResourceTargetType;
  payload: Record<string, unknown>;
  status: SuggestedResourceStatus;
  suggested_by_user_id: string;
  reviewer_user_id: string | null;
  reviewer_note: string | null;
  applied_target_id: string | null;
  /** Phase 1.8 — steward's edited payload (NULL = approved as submitted). */
  final_payload: Record<string, unknown> | null;
  /** Phase 1.8 — steward's type-switch result (NULL = unchanged). */
  final_target_type: SuggestedResourceTargetType | null;
  created_at: string;
  reviewed_at: string | null;
  applied_at: string | null;
  updated_at: string;
  suggester_display: string;
  reviewer_display: string | null;
}

export interface SuggestedResourcesListResponse {
  rows: SuggestedResource[];
  total: number;
  request_id: string;
}

export interface SuggestedResourcesCountResponse {
  count: number;
  request_id: string;
}

// =====================================================================
// Phase 1.14.3 — merge listings
// =====================================================================
// Mirrors the shapes in api/_shared/repos/providerMerge.ts. Kept here
// (rather than imported across the api/src boundary) per the project's
// boundary discipline: src/lib/types.ts is the single frontend mirror
// of API shapes. Drift surfaces at the integration test gate (which
// imports both sides) when adding a field; today no test catches the
// drift — the §m discipline is the binding.

export type MergeEventStatus = 'draft' | 'committed' | 'cancelled';

export interface MergeFieldDecision {
  winner_value: unknown;
  loser_value: unknown;
  chosen: 'winner' | 'loser' | 'custom';
  custom_value?: unknown;
}

export interface MergeFieldDecisions {
  main_row: Record<string, MergeFieldDecision>;
  // Empty for Phase 1.14.3 provider merges (junction conflicts auto-
  // resolve). Populated for Phase 2 entity classes with per-row picks.
  junction_decisions: unknown[];
}

export interface MergeDraftPlanField {
  field_name: string;
  winner_value: unknown;
  loser_value: unknown;
  status: 'agree' | 'auto_inherit_non_null' | 'conflict';
}

export interface MergeDraftPlan {
  winner_id: string;
  loser_id: string;
  winner_name: string;
  loser_name: string;
  winner_snapshot_at: string;
  loser_snapshot_at: string;
  fields: MergeDraftPlanField[];
  reparent_counts: {
    provider_notes: number;
    provider_individual_organizations: number;
    provider_service_areas: number;
    provider_specializations: number;
    provider_subcategorizations: number;
    provider_highlight_assignments: number;
    provider_service_model_assignments: number;
    work_item_attachments: number;
    suggested_edits: number;
    suggested_resources: number;
  };
}

export interface MergeProposeResponse {
  merge_event_id: string;
  plan: MergeDraftPlan;
}

export interface MergeEventSummary {
  id: string;
  tenant_id: string;
  target_type: 'organization';
  winner_id: string;
  loser_id: string;
  winner_name: string;
  loser_name: string;
  status: MergeEventStatus;
  proposed_by_user_id: string;
  proposed_at: string;
  committed_by_user_id: string | null;
  committed_at: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  field_decisions: MergeFieldDecisions;
  winner_snapshot_at: string;
  loser_snapshot_at: string;
  // Phase 1.14.3.1 — live blast-radius counts for the MergeReview
  // panel. Returned only on getMergeEvent for draft status (committed
  // and cancelled events don't reflect pending reparent work).
  reparent_counts?: MergeDraftPlan['reparent_counts'];
}
